import { m } from "framer-motion";
import { useState, useCallback, useEffect, useMemo } from "react";

import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import List from "@mui/material/List";
import Stack from "@mui/material/Stack";
import Badge from "@mui/material/Badge";
import Drawer from "@mui/material/Drawer";
import Divider from "@mui/material/Divider";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";

import { useBoolean } from "src/hooks/use-boolean";
import { useResponsive } from "src/hooks/use-responsive";

import Label from "src/components/label";
import Iconify from "src/components/iconify";
import Scrollbar from "src/components/scrollbar";
import { varHover } from "src/components/animate";

import NotificationItem from "./notification-item";
import { t } from "i18next";
import {
  playerNotificationsMarkAllRead,
  useGetPlayerNotifications,
} from "src/api/user";
import { CircularProgress } from "@mui/material";

// ----------------------------------------------------------------------

const TABS = [
  {
    value: "unread",
    label: "unread",
  },
  {
    value: "all",
    label: "all",
  },

  // {
  //   value: "archived",
  //   label: "archived",
  //   count: 10,
  // },
];

// ----------------------------------------------------------------------

export default function NotificationsPopover() {
  const drawer = useBoolean();

  const smUp = useResponsive("up", "sm");

  const [currentTab, setCurrentTab] = useState("unread");

  const handleChangeTab = useCallback(
    (event: React.SyntheticEvent, newValue: string) => {
      setCurrentTab(newValue);
    },
    []
  );

  const [allCount, setAllCount] = useState(0);

  const {
    notifications = [],
    notificationsLoading,
    refetch: refetchNotifications,
  } = useGetPlayerNotifications();

  const totalUnRead = notifications.filter((item) => item.status === 1).length;

  const handleMarkAllAsRead = async () => {
    try {
      await playerNotificationsMarkAllRead();

      // refresh list from backend so statuses become 0 in UI
      await refetchNotifications();
    } catch (error) {
      console.error("Failed to mark notifications as read", error);
      // optionally show snackbar/toast here
    }
  };

  useEffect(() => {
    if (notifications) {
      setAllCount(notifications.length);
    }
  }, [notifications]);

  const filteredNotifications = useMemo(() => {
    if (!notifications) return [];

    if (currentTab === "unread") {
      // status === 1 → unread
      return notifications.filter((item) => item.status === 1);
    }

    // "all"
    return notifications;
  }, [notifications, currentTab]);

  const renderHead = (
    <Stack
      direction="row"
      alignItems="center"
      sx={{ py: 2, pl: 2.5, pr: 1, minHeight: 68 }}
    >
      <Typography variant="h6" sx={{ flexGrow: 1 }}>
        {t("notifications")}
      </Typography>

      {!!totalUnRead && (
        <Tooltip title={t("mark_all_as_read")}>
          <IconButton color="primary" onClick={handleMarkAllAsRead}>
            <Iconify icon="eva:done-all-fill" />
          </IconButton>
        </Tooltip>
      )}

      {
        <IconButton onClick={drawer.onFalse}>
          <Iconify icon="mingcute:close-line" />
        </IconButton>
      }
    </Stack>
  );

  const renderTabs = (
    <Tabs value={currentTab} onChange={handleChangeTab}>
      {TABS.map((tab) => (
        <Tab
          key={tab.value}
          iconPosition="end"
          value={tab.value}
          label={t(tab.label)}
          icon={
            <Label
              variant={
                ((tab.value === "all" || tab.value === currentTab) &&
                  "filled") ||
                "soft"
              }
              color={
                (tab.value === "unread" && "info") ||
                (tab.value === "archived" && "success") ||
                "default"
              }
            >
              {tab.value === "all" && allCount}
              {tab.value === "unread" && totalUnRead}
            </Label>
          }
          sx={{
            "&:not(:last-of-type)": {
              mr: 3,
            },
          }}
        />
      ))}
    </Tabs>
  );

  const renderList = filteredNotifications && (
    <Scrollbar>
      <List disablePadding>
        {filteredNotifications.map((notification) => (
          <NotificationItem
            key={notification.id}
            notification={notification}
            refetchNotifications={refetchNotifications}
            onClose={drawer.onFalse}
          />
        ))}
      </List>
    </Scrollbar>
  );

  return (
    <>
      <IconButton
        component={m.button}
        whileTap="tap"
        whileHover="hover"
        variants={varHover(1.05)}
        color={drawer.value ? "primary" : "default"}
        onClick={drawer.onTrue}
      >
        <Badge badgeContent={totalUnRead} color="error">
          <Iconify icon="solar:bell-bing-bold-duotone" width={24} />
        </Badge>
      </IconButton>

      <Drawer
        open={drawer.value}
        onClose={drawer.onFalse}
        anchor="right"
        slotProps={{
          backdrop: { invisible: true },
        }}
        PaperProps={{
          sx: { width: 1, maxWidth: 420 },
        }}
      >
        {renderHead}

        <Divider />

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ pl: 2.5, pr: 1 }}
        >
          {renderTabs}
          {/* <IconButton onClick={handleMarkAllAsRead}>
            <Iconify icon="solar:settings-bold-duotone" />
          </IconButton> */}
        </Stack>

        <Divider />
        {notificationsLoading && (
          <Stack
            alignItems="center"
            justifyContent="center"
            sx={{ width: "100%", py: 4 }}
          >
            <CircularProgress />
          </Stack>
        )}
        {!notificationsLoading && renderList}

        {/* <Box sx={{ p: 1 }}>
          <Button fullWidth size="large">
            View All
          </Button>
        </Box> */}
      </Drawer>
    </>
  );
}
